/**
 * PRACTICE — past-paper questions, native on @ankr/glass.
 *
 * WHAT IT REPLACES AND WHY. The mode was labelled "Past papers" and rendered a list of drawing
 * sheets. That is a different capability wearing the wrong name, and a candidate who taps Practice
 * expecting questions and gets a drawing index has been told something untrue by the navigation.
 *
 * ── THE SHORTFALL IS THE PRODUCT, SO IT IS ON SCREEN ──────────────────────────────────────────
 * `/api/mcq/catalog` reports, per chapter, how many questions the corpus can actually SUPPORT
 * against how many were requested — and carries its own note: "produced is what the corpus can
 * actually support. A shortfall is declared, never padded — inventing distractors to reach 50 would
 * put faults in the paper."
 *
 * A surface that quietly showed 47 questions where 50 were asked for would throw that away. So the
 * shortfall is shown as a refusal beside the chapter, in the engine's own terms. It is the clearest
 * statement in the whole product that this system would rather be short than wrong.
 *
 * ── MARKING IS LOCAL BECAUSE THE ANSWER TRAVELS WITH THE QUESTION ─────────────────────────────
 * Each question ships `answer`, `answer_citation` and `answer_note`, and every OPTION carries its
 * own `from` and `citation`. So the comparison happens here — this is practice, not an examination,
 * and there is nothing to protect. `/api/mcq/check` is deliberately NOT called for this: it is a
 * paper-integrity check over a generated set, not an answer marker, and using it as one would be
 * reading a different instrument.
 *
 * Every revealed answer shows its citation. A right answer a candidate cannot trace is a right
 * answer he cannot revise from.
 */

import { useState, useMemo } from 'react';
import { Value, useWidgetData } from '../../../packages/ankr-glass/src/react.jsx';
import { plan } from '../../../packages/ankr-vahana/src/plan.mjs';
import { cited, refused } from '../../../packages/ankr-provenance/src/provenance.mjs';
import { API } from './surfaces.jsx';
import PastPapers from './pastpapers.jsx';
import GatedPanel from './gated.jsx';
import { authHeaders } from './identity.mjs';

const W = {
  /* COMPILED RETRIEVAL PRACTICE (P3, 2026-08-15). Items made from a course's own cited sections,
     so every course in the registry gets practice without any being authored by hand. */
  compiled: { id: 'practice-compiled', kind: 'value',
              wire: { kind: 'rest', route: '/api/practice/compiled', method: 'GET', field: 'items' } },
  catalog: { slug: 'mcq-catalogue', layout: 'auto-table',
             wire: { kind: 'rest', route: '/api/mcq/catalog', method: 'GET', field: 'catalog' } },
  paper:   { slug: 'mcq-paper', layout: 'auto-table',
             wire: { kind: 'rest', route: '/api/mcq', method: 'GET', field: 'questions' } },
};

const TRACKS = [
  { id: 'maritime', label: '2nd Mate' },
  { id: 'cm',       label: 'Chief Mate' },
  { id: 'master',   label: 'Master' },
  { id: 'code101',  label: 'Code 101' },
];

/** One question. The answer is revealed on demand, never before — and always with its citation. */
function Question({ q, n, picked, onPick, revealed }) {
  const right = picked && picked === q.answer;
  return (
    <div className="g-glass shell-panel prac-q">
      <div className="prac-q-head">
        <span className="g-label">Question {n}</span>
        {revealed && (
          <span className={`g-chip ${right ? 'g-chip-computed' : 'g-chip-warn'}`}>
            {right ? 'correct' : `answer ${q.answer}`}
          </span>
        )}
      </div>
      <p className="g-body prac-stem">{q.stem}</p>

      <div className="prac-options">
        {(q.options || []).map((o) => {
          const chosen = picked === o.label;
          const isAnswer = revealed && o.label === q.answer;
          return (
            <button key={o.label} disabled={revealed}
              className={`prac-opt ${chosen ? 'chosen' : ''} ${isAnswer ? 'answer' : ''} ${
                revealed && chosen && !right ? 'wrong' : ''}`}
              onClick={() => onPick(o.label)}>
              <span className="prac-opt-label g-mono">{o.label}</span>
              <span className="prac-opt-text">{o.text}</span>
              {/* Each option carries where it came from. Shown once revealed, so a candidate can go
                  back to the source of the one he chose as well as the one that was right. */}
              {revealed && o.citation && (
                <span className="prac-opt-cite g-dim">{o.citation}</span>
              )}
            </button>
          );
        })}
      </div>

      {revealed && (q.answer_citation || q.answer_note) && (
        <div className="prac-why">
          {q.answer_citation && <Value of={cited(q.answer_note || 'the answer', q.answer_citation)} />}
          {q.answer_note && !q.answer_citation && <p className="g-body">{q.answer_note}</p>}
        </div>
      )}
    </div>
  );
}

export default function PracticeSurface() {
  /* TWO KINDS OF PRACTICE, AND THEY MUST NOT BE CONFUSED. 'generated' is our own MCQ paper compiled
     from the corpus; 'real' is the MMD past-paper bank — questions an examiner actually set. This
     panel used to be headed "Past papers" while serving the generated ones, which was fine when
     there were no real ones on the surface and is not fine now. */
  const [kind, setKind] = useState('generated');
  const [track, setTrack] = useState('maritime');
  const [chapter, setChapter] = useState(null);
  const [picks, setPicks] = useState({});
  const [revealed, setRevealed] = useState(false);

  const cat = useWidgetData(W.catalog, {}, { baseUrl: API, plan });
  const t = cat.data?.[track];
  const rows = t?.rows || [];

  /* The principal travels with the request — see the note in learn.jsx. A paper is gated on the
     same rule a chapter is, so it failed the same way and for the same reason. */
  const auth = authHeaders();
  const compiled = useWidgetData(W.compiled, chapter ? { chapter, level: (track || '2m').toUpperCase() } : {},
                                 { baseUrl: API, plan, enabled: Boolean(chapter), headers: auth });
  const paper = useWidgetData(W.paper, chapter ? { chapter, n: 10, track } : {},
                              { baseUrl: API, plan, enabled: Boolean(chapter), headers: auth });
  const questions = paper.data?.questions || [];

  // A new chapter is a new attempt. Carrying answers across would score one paper against another.
  const start = (ch) => { setChapter(ch); setPicks({}); setRevealed(false); };

  const score = useMemo(
    () => questions.filter((q) => picks[q.id] && picks[q.id] === q.answer).length,
    [questions, picks]);
  const answered = questions.filter((q) => picks[q.id]).length;

  if (chapter) {
    return (
      <div className="shell-col g-rise">
        <div className="g-glass shell-panel">
          <div className="prac-q-head">
            <div>
              <div className="g-label">{TRACKS.find((x) => x.id === track)?.label} · chapter {chapter}</div>
              <h2 className="g-h2" style={{ marginTop: 'var(--g-2)' }}>{paper.data?.title || ''}</h2>
            </div>
            <div className="shell-picker">
              <button className="g-btn" onClick={() => setChapter(null)}>Back to chapters</button>
              {/* THE WHOLE PAPER, beside the drill. Practice serves ten at a time and marks them;
                  /question-paper is every question the chapter can support, with the source and
                  citation under each option and the keys switchable off — the version you print or
                  hand to a candidate. Same engine, so there is no second set of questions to drift.
                  Opened in a new tab rather than navigated to, because leaving mid-attempt would
                  throw away answers the learner has already given. */}
              <a className="g-btn" href={`/question-paper?chapter=${chapter}&track=${track}`}
                 target="_blank" rel="noopener noreferrer">Whole paper ↗</a>
              <button className="g-btn g-btn-primary" disabled={!answered || revealed}
                onClick={() => setRevealed(true)}>
                {revealed ? 'Answers shown' : `Check ${answered} answered`}
              </button>
            </div>
          </div>

          {/* ── COMPILED RETRIEVAL PRACTICE (P3, 2026-08-15) ─────────────────────────────────────
              Made from this chapter's OWN cited sections, so every course gets practice without any
              being authored by hand. Each item carries the sentence it came from, because a
              question whose answer you cannot check against its source is a claim, not a question.
              RENDERED, not merely fetched: three times this session something was built, wired and
              unreachable, and the founder found each by clicking. */}
          {(compiled.data || []).length > 0 && (
            <div className="g-glass shell-panel" style={{ marginTop: 'var(--g-3)' }}>
              <div className="g-label">
                Retrieval practice · {(compiled.data || []).length} compiled from this chapter's own sources
              </div>
              <p className="g-body g-dim">
                Recall the missing term. Every answer is a word from the passage cited beneath it —
                open the reading and check, rather than taking the question's word for it.
              </p>
              {(compiled.data || []).slice(0, 8).map((it, i) => (
                <details key={i} className="cou-tr" style={{ marginTop: 'var(--g-2)' }}>
                  <summary className="g-body">{it.asked}</summary>
                  <p className="g-body" style={{ marginTop: 'var(--g-2)' }}>
                    <b>{it.answer}</b>
                    {it.options ? <span className="g-dim"> — offered with {it.options.filter((o) => o !== it.answer).join(', ')}</span> : null}
                  </p>
                  <p className="g-body g-dim">{it.sentence}</p>
                  <p className="cite-note"><Value of={cited(String(it.citation), String(it.citation))} /></p>
                </details>
              ))}
            </div>
          )}
          {compiled.data && compiled.data.length === 0 && (
            <div style={{ marginTop: 'var(--g-3)' }}>
              <Value of={refused('nothing in this chapter\'s sources yields a term distinctive enough to ask for — said plainly rather than padded with a guess')} />
            </div>
          )}

          {/* The engine's own shortfall, in its own words, before he starts. */}
          {paper.data && paper.data.shortfall > 0 && (
            <div style={{ marginTop: 'var(--g-3)' }}>
              <Value of={refused(
                `${paper.data.produced} questions where ${paper.data.requested} were requested. ` +
                `The corpus supports ${paper.data.entries_available} entries for this chapter, and a ` +
                `shortfall is declared rather than padded — inventing distractors to reach the number ` +
                `would put faults in the paper.`)} />
            </div>
          )}

          {revealed && (
            <div className="prac-score">
              <span className="prac-score-n">{score}</span>
              <span className="g-dim"> of {questions.length}</span>
            </div>
          )}
        </div>

        {/* A GATED PAPER IS THE SAME MOMENT AS A GATED CHAPTER, so it gets the same panel rather
            than the bare chip the envelope would otherwise render. Practice is arguably where it
            matters more: a candidate who came here to sit questions has already decided to work. */}
        {paper.envelope?.state === 'gated'
          ? <div className="g-glass shell-panel">
              <GatedPanel envelope={paper.envelope} what="This paper" />
            </div>
          : paper.envelope ? <div className="g-glass shell-panel"><Value of={paper.envelope} /></div>
          : paper.loading ? <div className="g-glass shell-panel gl-empty g-body">Loading the paper…</div>
          : questions.map((q, i) => (
            <Question key={q.id} q={q} n={i + 1} revealed={revealed} picked={picks[q.id]}
              onPick={(l) => setPicks((p) => ({ ...p, [q.id]: l }))} />
          ))}
      </div>
    );
  }

  const kindPicker = (
    <div className="shell-picker" style={{ marginTop: 'var(--g-3)' }}>
      <button className={`g-btn ${kind === 'generated' ? 'g-btn-primary' : ''}`}
        onClick={() => setKind('generated')}>Generated from the corpus</button>
      <button className={`g-btn ${kind === 'real' ? 'g-btn-primary' : ''}`}
        onClick={() => setKind('real')}>Real MMD past papers</button>
    </div>
  );

  if (kind === 'real') {
    return (
      <div className="shell-col g-rise">
        <div className="g-glass shell-panel">
          <div className="g-label">Practice</div>
          <h2 className="g-h2" style={{ margin: 'var(--g-2) 0 var(--g-3)' }}>Two kinds of paper</h2>
          <p className="g-body">
            <b>Generated</b> papers are compiled from our corpus, so every option carries a citation.
            <b> Real MMD papers</b> are questions an examiner actually set — they are harder, they are
            not multiple choice, and we cannot answer all of them. Both are useful and they are not
            the same thing.
          </p>
          {kindPicker}
        </div>
        <PastPapers />
      </div>
    );
  }

  return (
    <div className="shell-col g-rise">
      <div className="g-glass shell-panel">
        <div className="g-label">Practice · generated</div>
        <h2 className="g-h2" style={{ margin: 'var(--g-2) 0 var(--g-3)' }}>Choose a chapter</h2>
        <p className="g-body">
          Every question is generated from the corpus and carries its source. Where a chapter cannot
          support the number asked for, the shortfall is declared rather than padded.
        </p>
        {kindPicker}
        <div className="shell-picker" style={{ marginTop: 'var(--g-3)' }}>
          {TRACKS.map((x) => (
            <button key={x.id} onClick={() => setTrack(x.id)}
              className={`g-btn ${track === x.id ? 'g-btn-primary' : ''}`}>{x.label}</button>
          ))}
        </div>
      </div>

      {cat.envelope ? <div className="g-glass shell-panel"><Value of={cat.envelope} /></div> : (
        <div className="g-glass shell-panel">
          <div className="g-label">
            {t ? `${t.produced} questions across ${t.chapters} chapters` : 'Catalogue'}
            {t?.shortfall ? ` · ${t.shortfall} short of ${t.per_chapter * t.chapters}` : ''}
          </div>
          <div className="prac-chapters">
            {/* ROW = the drill button PLUS a link to the whole paper. The row used to be a bare
                <button>; putting an anchor inside one is invalid markup and browsers resolve it
                unpredictably, so the row is a container and the button is one of its children. */}
            {rows.map((r) => (
              <div key={r.chapter} className="prac-chapter-row">
              <button className="prac-chapter" onClick={() => start(r.chapter)}>
                <span className="prac-ch-n g-mono">{r.chapter}</span>
                <span className="prac-ch-title">{r.title}</span>
                <span className="prac-ch-meta">
                  <span className="g-mono">{r.produced}</span>
                  {/* `short` is the engine's own word for the state, so it is used rather than
                      paraphrased into something softer. */}
                  {r.status === 'short' && (
                    <span className="g-chip g-chip-null" title={
                      `${r.produced} of ${r.requested} — ${r.entries_available} corpus entries available`}>
                      short {r.shortfall}
                    </span>
                  )}
                </span>
              </button>
              <a className="prac-ch-paper" href={`/question-paper?chapter=${r.chapter}&track=${track}`}
                 target="_blank" rel="noopener noreferrer"
                 title={`The whole ${r.produced}-question paper for chapter ${r.chapter}, with every option's source`}>
                paper ↗
              </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
